import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import styles from './LeadForm.module.css';
import { trackEvent } from '../firebase';

// ─────────────────────────────────────────────
// Opciones de rol
// ─────────────────────────────────────────────
const ROLES = [
  { value: '', label: '¿Quién sos?' },
  { value: 'usuario',    label: 'Pasajero habitual' },
  { value: 'empresa',    label: 'Empresa de buses' },
  { value: 'inversor',   label: 'Inversor / Inversionista' },
  { value: 'municipio',  label: 'Municipalidad / Gobierno' },
  { value: 'otro',       label: 'Otro' },
];

// ─────────────────────────────────────────────
// Servicio de envío — integrá aquí tu backend,
// Formspree, EmailJS, etc.
// ─────────────────────────────────────────────
// ─── Formspree ID ────────────────────────────────────────────────────────────
const FORMSPREE_ID =
  process.env.REACT_APP_FORMSPREE_ID || 'xreorbez';   // ✅ Tu form ID real

async function sendLead({ name, email, role }) {
  const res = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body:    JSON.stringify({ name, email, role }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.error || 'Error al enviar el formulario');
  }
  return res.json();
  /*
   * OPCIÓN FUTURA — Tu API en OCI / Oracle APEX:
   * const res = await fetch(`${process.env.REACT_APP_APEX_API_URL}/leads`, {
   *   method: 'POST',
   *   headers: { 'Content-Type': 'application/json' },
   *   body: JSON.stringify({ name, email, role }),
   * });
   */
}

// ─────────────────────────────────────────────
// Componente
// ─────────────────────────────────────────────
export function LeadForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState('');
  const [serverError, setServerError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({ mode: 'onBlur' });

  const nameValue = watch('name', '');

  const onSubmit = async (data) => {
    setServerError('');
    try {
      await sendLead(data);
      // 📊 Firebase Analytics — evento de conversión
      trackEvent('lead_submitted', { role: data.role || 'no_especificado' });
      setSubmittedName(data.name || 'mae');
      setSubmitted(true);
    } catch (err) {
      trackEvent('lead_error', { reason: err.message });
      setServerError('Hubo un problema al enviar. Intentá de nuevo.');
    }
  };

  if (submitted) {
    return (
      <div className={styles.success} role="alert" aria-live="polite">
        <span className={styles.successIcon} aria-hidden="true">✓</span>
        <p className={styles.successTitle}>¡Listo, {submittedName}!</p>
        <p className={styles.successSub}>Te avisamos en cuanto lancemos. ¡Pura vida! 🇨🇷</p>
      </div>
    );
  }

  return (
    <form
      className={styles.form}
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      aria-label="Formulario de registro de interés"
    >
      {/* Fila superior */}
      <div className={styles.topRow}>
        <div className={styles.fieldWrap}>
          <input
            className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
            type="text"
            placeholder="Tu nombre"
            aria-label="Nombre"
            {...register('name', { required: 'Tu nombre es requerido' })}
          />
          {errors.name && (
            <span className={styles.errorMsg} role="alert">{errors.name.message}</span>
          )}
        </div>

        <div className={styles.fieldWrap}>
          <select
            className={`${styles.select} ${errors.role ? styles.inputError : ''}`}
            aria-label="¿Quién sos?"
            {...register('role', { required: 'Seleccioná tu perfil' })}
            defaultValue=""
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value} disabled={r.value === ''}>
                {r.label}
              </option>
            ))}
          </select>
          {errors.role && (
            <span className={styles.errorMsg} role="alert">{errors.role.message}</span>
          )}
        </div>
      </div>

      {/* Fila de correo + botón */}
      <div className={`${styles.emailRow} ${errors.email ? styles.emailRowError : ''}`}>
        <input
          className={styles.emailInput}
          type="email"
          placeholder="tucorreo@ejemplo.com"
          aria-label="Correo electrónico"
          {...register('email', {
            required: 'El correo es requerido',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Ingresá un correo válido',
            },
          })}
        />
        <button
          type="submit"
          className={styles.btn}
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? 'Enviando…' : 'Quiero saber más →'}
        </button>
      </div>

      {errors.email && (
        <span className={styles.errorMsg} role="alert">{errors.email.message}</span>
      )}

      {serverError && (
        <p className={styles.serverError} role="alert">{serverError}</p>
      )}

      <p className={styles.note}>
        Sin spam. Solo te avisamos cuando lancemos. Prometido. 🤝
      </p>
    </form>
  );
}
